import { DEFAULT_FAVICON } from "@/shared/constants";
import { getStoryDisplayTitle } from "@/shared/utils/story-display-title";
import type { IProfileData, TOrgInfo } from "@/types";
import type { StoryType } from "@config";
import { escapeBakedHtml } from "../utils/resolve-system-tokens";

import {
  analyticsData,
  gatedContentBody,
  gatedContentData,
  handleDesignSystem,
  languageToHtmlLang,
  loaderGlobalData,
  pdfData,
  toInlineScriptJson,
} from "./utils";

type TemplateProps = {
  story: StoryType;
  bundleCss: string;
  bundleJs: string;
  senderInformation: IProfileData;
  orgInfo: TOrgInfo;
  isPreview: boolean;
};

export const stagingTemplate = ({
  story,
  bundleCss,
  bundleJs,
  orgInfo,
  isPreview,
  senderInformation,
}: TemplateProps) => {
  const { settings } = story;
  const randomNumber = new Date().toISOString();
  const analyticsFile = `https://www.storydoc.com/assets/editor/global/js/analytics.js?${randomNumber}`;
  const favicon = settings.favicon || orgInfo?.favicon || DEFAULT_FAVICON;
  // Same name the published page gets from the server's {{system.title}},
  // escaped like it (<title> and quoted content attributes) and token-inert.
  const previewTitle = escapeBakedHtml(getStoryDisplayTitle(settings) ?? "");

  const hasDocumentHeader = !!settings.engagementBar?.active;
  // The floating menu lives inside engagement-bar.js, so that script has to load
  // even when the document header is off — it hides the bar itself when
  // engagementBar.active is false. progress-bar.js stays gated on the header
  // alone: it renders its bar unconditionally and the floating menu never uses it.
  const hasFloatingMenu =
    !!settings.menu?.active && settings.menu.type === "floating";

  const storyJson = toInlineScriptJson({
    ...story,
    orgInfo: {
      hideFooterBadge: orgInfo?.hideFooterBadge || false,
      hideHeaderBadge: orgInfo?.hideHeaderBadge || false,
    },
  });

  const header = `      
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="robots" content="noindex, nofollow">
      <link rel="canonical" href="{{system.canonicalUrl}}">
      <title>${isPreview ? previewTitle : "{{system.title}}"}</title>
      <link rel="stylesheet" href="${bundleCss}?${randomNumber}"> 

      <!-- Favicon -->
        <link rel="icon" href="${favicon}" />
        <link rel="apple-touch-icon" href="${favicon}" />
        <link rel="apple-touch-icon" sizes="72x72" href="${favicon}" />
        <link rel="apple-touch-icon" sizes="114x114" href="${favicon}" />
      <!-- END Favicon -->

      <!-- Open Graph (facebook and whatsapp preview) -->
      <meta property="og:locale" content="${settings.og.locale}" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="${isPreview ? previewTitle : "{{system.title}}"}" />
      <meta property="og:description" content="${settings.og.description}" />
      <meta property="og:url" content="{{system.canonicalUrl}}" />
      <meta property="og:site_name" content="${isPreview ? previewTitle : "{{system.title}}"}" />
      <meta property="og:image" name="image" content="{{system.ogImage}}" />
      <meta property="og:image:secure_url" content="{{system.ogImage}}" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="1200" />
      <!-- Open Graph END (facebook and whatsapp preview) -->
      ${settings.headerCode ? settings.headerCode : ""}
      ${gatedContentData}
      ${pdfData}
      ${handleDesignSystem(story, orgInfo)}
      ${settings.engagementBar?.active ? "<style>.storydoc-badge-footer, .progress-bar {display: none !important;}</style>" : ""}
      ${loaderGlobalData({ orgInfo })}
    </head>`;

  const body = `
    <body class="sd-presentation">
      <script src="https://www.storydoc.com/assets/editor/global/js/loader.js"></script>
      <script defer src="${bundleJs}?${randomNumber}" crossorigin type="module"></script>
      ${gatedContentBody}
      <div id="presentation-root"></div>

      <script>
        window.story = ${storyJson};
        window.__SD_DATA__ = "[[__SD_DATA__]]";
        window.__SD_SHORT_URL__ = "[[__SD_SHORT_URL__]]";
        ${
          isPreview
            ? `
          window.__SD_SENDER_INFO__ = ${JSON.stringify(senderInformation.sender)}
          window.__SD_ACCOUNT_INFO__ = ${JSON.stringify(senderInformation.org)}
        `
            : `
          window.__SD_SENDER_INFO__ = "[[__SD_SENDER_INFO__]]";
          window.__SD_ACCOUNT_INFO__ = "[[__SD_ACCOUNT_INFO__]]";
        `
        }
      </script>
      ${analyticsData(story.orgId, story.settings.title, orgInfo?.title, story._id)}
      <script async type="text/javascript" src="${analyticsFile}"></script>
      ${settings?.footerCode || ""}
        <script>
          document.addEventListener('DOMContentLoaded', function () {
            let linkTags = document.querySelectorAll("a");
            if (!linkTags.length) return
            linkTags.forEach(function (link) {
              var href = link.getAttribute('href');
              if (!href) return
        
              if (window.eventLabel === '36a3a2a42b5b702f') websiteTargetHandler(link, href)
              else storyTargetHandler(link, href)
            });
        
            function storyTargetHandler(link, href) {
              if (href.indexOf('http') > -1) link.setAttribute('target', '_blank')
              else link.removeAttribute('target')
            }
        
            function websiteTargetHandler(link, href) {
              if(link.getAttribute('target')) return
              if (href.indexOf('http') > -1 && href.indexOf('storydoc.com') <= -1) link.setAttribute('target', '_blank')
              else link.removeAttribute('target')
            }
          })
        </script>
        ${
          hasDocumentHeader
            ? `<script src="https://www.storydoc.com/assets/editor/staging/js/progress-bar.js"></script>`
            : ""
        }
        ${
          hasDocumentHeader || hasFloatingMenu
            ? `<script src="https://www.storydoc.com/assets/editor/staging/js/engagement-bar.js"></script>`
            : ""
        }
    </body>
  `;

  const htmlLang = languageToHtmlLang(settings.language);

  return `
    <!DOCTYPE html>
      <html lang="${htmlLang}">
        ${header}
        ${body}
      </html>
  `;
};
